// frontend/src/components/Apresentacao/Apresentacao.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicNav } from '../layout/PublicNav/PublicNav';
import './Apresentacao.css';

// Cases de Sucesso com tom de "Testado e Aprovado"
const SUCCESS_CASES = [
  {
    icon: '🏠',
    title: 'Avaliação de Imóveis (Regressão)',
    model: 'Regressão Linear',
    color: '#4fc3f7',
    status: 'TESTADO E APROVADO',
    problem: 'Uma imobiliária de médio porte demorava em média 3 dias para avaliar o preço de um imóvel. Isso travava o processo de venda e fazia com que corretores perdessem oportunidades.',
    solution: 'A AutoSAGE processou o dataset `dataset_linear_regression` (500 registros), treinou um modelo de Regressão Linear e agora entrega a avaliação instantânea.',
    result: 'Tempo de avaliação caiu de 3 dias para 2 segundos. O modelo obteve um RMSE de 11.108, garantindo precisão na estimativa.',
    traceId: 'trace-1791832315973-5oc1cz3f',
    metric: 'RMSE: 11.108'
  },
  {
    icon: '📡',
    title: 'Previsão de Cancelamento (Churn)',
    model: 'Regressão Logística',
    color: '#ffc107',
    status: 'TESTADO E APROVADO',
    problem: 'Uma operadora de telecom tinha um alto índice de cancelamento (churn). A empresa só descobria que o cliente ia sair depois que ele já havia solicitado o cancelamento.',
    solution: 'A AutoSAGE processou o dataset `dataset_logistic_regression` (1.000 registros), treinou um modelo de Regressão Logística e agora prevê com precisão quem vai cancelar.',
    result: 'A equipe de retenção passou a agir de forma proativa. O modelo alcançou um ROC-AUC de 1.0000, a maior acurácia possível para este tipo de problema.',
    traceId: 'trace-1791832480606-nl703i8p',
    metric: 'ROC-AUC: 1.0000'
  },
  {
    icon: '🏦',
    title: 'Análise de Crédito (Classificação)',
    model: 'Random Forest',
    color: '#66bb6a',
    status: 'TESTADO E APROVADO',
    problem: 'Um banco digital precisava escalar a concessão de empréstimos, mas o modelo de análise de crédito baseado em regras fixas rejeitava bons pagadores e aprovava maus pagadores.',
    solution: 'A AutoSAGE processou o dataset `dataset_random_forest_classification` (1.000 registros) e treinou um modelo Random Forest, capturando interações complexas que as regras manuais não viam.',
    result: 'Redução significativa na inadimplência e aprovação mais justa. O modelo alcançou um ROC-AUC de 1.0000, demonstrando separação perfeita das classes.',
    traceId: 'trace-1791832698295-y1152xob',
    metric: 'ROC-AUC: 1.0000'
  },
  {
    icon: '🛡️',
    title: 'Custo de Reparo (Regressão)',
    model: 'Random Forest',
    color: '#ab47bc',
    status: 'TESTADO E APROVADO',
    problem: 'Uma seguradora de imóveis não conseguia estimar com precisão o custo de reparo de um sinistro. As reservas de caixa eram sempre superestimadas, imobilizando capital.',
    solution: 'A AutoSAGE processou o dataset `dataset_random_forest_regression` (1.000 registros) e treinou um modelo Random Forest para prever o custo do reparo com base nas características do imóvel.',
    result: 'Redução de 15% no capital retido. O modelo obteve um RMSE de 11.071,30, uma das melhores performances para este dataset.',
    traceId: 'trace-1791832770078-ls2xcj0e',
    metric: 'RMSE: 11.071,30'
  },
  {
    icon: '⚙️',
    title: 'Manutenção Preditiva (Classificação)',
    model: 'XGBoost',
    color: '#ef5350',
    status: 'TESTADO E APROVADO',
    problem: 'Uma indústria metalúrgica sofria com paradas não planejadas de suas máquinas. Cada hora de linha parada custava R$ 50.000. A manutenção era feita por tempo, não por necessidade.',
    solution: 'A AutoSAGE processou o dataset `dataset_xgboost_classification` (1.000 registros) e treinou um modelo XGBoost, capaz de identificar padrões sutis que antecedem a falha.',
    result: 'Redução de 40% no tempo de máquina parada. O modelo alcançou um ROC-AUC de 1.0000, identificando corretamente todas as falhas.',
    traceId: 'trace-1791832954644-7ckow079',
    metric: 'ROC-AUC: 1.0000'
  },
  {
    icon: '📊',
    title: 'Previsão de Sinistros (Contagem)',
    model: 'Poisson',
    color: '#26a69a',
    status: 'TESTADO E APROVADO',
    problem: 'Uma seguradora de automóveis não sabia precificar corretamente o seguro. Clientes com perfil de risco alto pagavam o mesmo que clientes seguros, causando prejuízo.',
    solution: 'A AutoSAGE processou o dataset `dataset_poisson_regression` (500 registros) e treinou um modelo Poisson para prever exatamente quantos sinistros cada cliente abriria no ano.',
    result: 'Precificação justa e individualizada. O modelo obteve um Deviance de 895,62, demonstrando excelente ajuste aos dados de contagem.',
    traceId: 'trace-1791832628749-93m05yzt',
    metric: 'Deviance: 895,62'
  },
  {
    icon: '🔮',
    title: 'Previsão de Demanda (Série Temporal)',
    model: 'ARIMA',
    color: '#ff7043',
    status: 'TESTADO E APROVADO',
    problem: 'Uma rede varejista não sabia quanto comprar de estoque para datas comemorativas. Errava para mais (encalhe) ou para menos (ruptura).',
    solution: 'A AutoSAGE processou o dataset `dataset_time_series` (1.000 registros) e treinou um modelo de Série Temporal (ARIMA/Prophet) que capturou a sazonalidade e a tendência de crescimento.',
    result: 'Redução de 20% no estoque encalhado e zero rupturas. O modelo obteve um MAPE de 0,0697 (6,97% de erro), uma precisão excepcional para previsão de demanda.',
    traceId: 'trace-1791832878742-h02kd8r4',
    metric: 'MAPE: 0,0697'
  },
];

export const Apresentacao: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="apresentacao-page">
      <PublicNav />
      <div className="apresentacao-container">

        {/* HERO SECTION */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-badge">✨ AutoSAGE · A Inteligência Artificial que sua empresa pode auditar</div>
            <h1>
              Pare de perder dinheiro<br />
              com decisões no escuro.
            </h1>
            <p className="hero-subtitle">
              Transforme seus dados em decisões <strong>seguras, auditáveis e lucrativas</strong> em minutos.
              Sem código, sem caixa-preta, com governança total.
            </p>
            <div className="hero-actions">
              <button className="btn-cta-primary" onClick={() => navigate('/register')}>
                🚀 Começar Grátis
              </button>
              <button className="btn-cta-secondary" onClick={() => navigate('/login')}>
                🔐 Já tenho conta
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="floating-card card-1">
              <span className="icon">📈</span>
              <div>
                <strong>+15% de margem</strong>
                <p>Previsão de vendas</p>
              </div>
            </div>
            <div className="floating-card card-2">
              <span className="icon">🛡️</span>
              <div>
                <strong>100% Auditável</strong>
                <p>Cada decisão rastreada</p>
              </div>
            </div>
            <div className="floating-card card-3">
              <span className="icon">⏱️</span>
              <div>
                <strong>Em minutos</strong>
                <p>Do CSV ao modelo</p>
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO DE DORES */}
        <section className="section-pains">
          <h2>Sua empresa ainda sofre com isso?</h2>
          <div className="pains-grid">
            <div className="pain-card">
              <span className="pain-icon">😰</span>
              <h3>Decisões no escuro</h3>
              <p>Você tem dados, mas não sabe como usá-los para prever o futuro.</p>
            </div>
            <div className="pain-card">
              <span className="pain-icon">💸</span>
              <h3>Dinheiro perdido</h3>
              <p>Clientes cancelam, máquinas quebram, estoques encalham. E você só descobre depois.</p>
            </div>
            <div className="pain-card">
              <span className="pain-icon">🕵️</span>
              <h3>Caixa-preta</h3>
              <p>A IA do mercado não explica o porquê das decisões. Impossível auditar.</p>
            </div>
          </div>
        </section>

        {/* CASES DE SUCESSO DETALHADOS */}
        <section className="section-cases">
          <h2>Execuções Reais da Plataforma</h2>
          <p className="section-sub">Casos reais que já rodaram na AutoSAGE e estão registrados no banco de dados com trace_id auditável.</p>
          <div className="success-cases-grid">
            {SUCCESS_CASES.map((c, i) => (
              <div key={i} className="success-case-card" style={{ borderTopColor: c.color }}>
                <div className="success-case-header">
                  <span className="success-case-icon" style={{ color: c.color }}>{c.icon}</span>
                  <div style={{ flex: 1 }}>
                    <h3>{c.title}</h3>
                    <span className="success-case-model" style={{ color: c.color }}>
                      {c.model}
                    </span>
                  </div>
                  <span className="success-case-status" style={{ background: 'rgba(102, 187, 106, 0.15)', color: '#66bb6a', border: '1px solid #66bb6a' }}>
                    {c.status}
                  </span>
                </div>

                <div className="success-case-section">
                  <strong>🚨 O Problema:</strong>
                  <p>{c.problem}</p>
                </div>

                <div className="success-case-section">
                  <strong>💡 A Solução:</strong>
                  <p>{c.solution}</p>
                </div>

                <div className="success-case-result">
                  <strong>📈 O Resultado:</strong>
                  <p>{c.result}</p>
                </div>

                <div className="success-case-metric">
                  <strong>Métrica do Modelo:</strong>
                  <span style={{ color: c.color }}>{c.metric}</span>
                </div>

                <details className="technical-details">
                  <summary>🔬 Ver detalhes técnicos (Trace ID)</summary>
                  <p><code>{c.traceId}</code></p>
                </details>
              </div>
            ))}
          </div>
        </section>

        {/* GOVERNANÇA E AUDITORIA */}
        <section className="section-governance">
          <h2>Por que a AutoSAGE é segura?</h2>
          <p className="section-sub">Rastreabilidade completa: do dado bruto à decisão final.</p>
          <div className="governance-grid">
            <div className="governance-card">
              <span className="gov-icon">🔍</span>
              <h3>Trace ID Único</h3>
              <p>Cada decisão tem um código de rastreio. Saiba exatamente qual dado foi usado.</p>
            </div>
            <div className="governance-card">
              <span className="gov-icon">📋</span>
              <h3>Auditoria Imutável</h3>
              <p>Histórico completo de todas as transformações. Pronto para o compliance.</p>
            </div>
            <div className="governance-card">
              <span className="gov-icon">⚖️</span>
              <h3>Separação de Decisões</h3>
              <p>A preparação dos dados decide. O modelo apenas executa. Sem conflito de interesses.</p>
            </div>
            <div className="governance-card">
              <span className="gov-icon">🔒</span>
              <h3>Dados Isolados</h3>
              <p>Cada empresa vê apenas os seus próprios dados. Segurança de nível bancário.</p>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="cta-final">
          <h2>Pronto para tomar decisões com confiança?</h2>
          <p>Junte-se às empresas que já transformaram dados em lucro com a AutoSAGE.</p>
          <button className="btn-cta-primary" onClick={() => navigate('/register')}>
            🚀 Criar minha conta agora
          </button>
          <p className="cta-hint">Sem cartão de crédito. Sem instalação. 100% online.</p>
        </section>

      </div>
    </div>
  );
};

export default Apresentacao;